<script lang="ts">
  import { t } from '$lib/i18n';
  import { drawer } from '$lib/stores/drawer';
  import { createInfiniteQuery } from '@tanstack/svelte-query';
  import Button from '$components/ui/button.svelte';
  import Icon from '$components/ui/icon/icon.svelte';
  import { getReviewsData, type Reviews } from '$lib/yotpo';
  import Select from '$components/ui/select/select.svelte';
  import Skeleton from '$components/ui/skeleton.svelte';
  import YotpoReviews from '$components/blocks/yotpo-reviews.svelte';
  import { cva } from 'class-variance-authority';
  import { cn } from '$lib/utils';

  export let productId: string;
  export let productTitle: string;
  export let productType: string;
  export let productVariant: string;

  type StarItemKeys = '1' | '2' | '3' | '4' | '5';
  let star: StarItemKeys | 'all' = 'all';
  let sort: 'default' | 'date' | 'rating' = 'default';
  let starDistribution: Record<StarItemKeys, number> | undefined = undefined;

  const getTotal = (page: Reviews, filter: typeof star) => {
    if (filter === 'all') return page.pagination.total;

    return page.bottomline.star_distribution[filter];
  };

  $: averageScore = 0;

  $: client = createInfiniteQuery<Reviews>({
    staleTime: Infinity,
    queryKey: ['reviews', productId, { star, sort }],
    queryFn: async ({ pageParam = 1 }) => {
      return await getReviewsData({
        id: productId,
        page: pageParam,
        star: star === 'all' ? undefined : star,
        sort
      });
    },
    getNextPageParam: (lastPage) => {
      if (
        lastPage.pagination.page <
        Math.ceil(getTotal(lastPage, star) / lastPage.pagination.per_page)
      ) {
        return lastPage.pagination.page + 1;
      }
    }
  });

  $: if (!$client.isLoading) {
    averageScore = $client.data?.pages[0].bottomline.average_score || 0;
    starDistribution = $client.data?.pages[0].bottomline.star_distribution;
  }

  $: showing = ($client.data?.pages || []).reduce((acc, page) => acc + page.reviews.length, 0);
  $: total = $client.data ? getTotal($client.data.pages[$client.data.pages.length - 1], star) : 0;
  const getStarItemTyped = (starItem: string) => {
    return starItem as StarItemKeys;
  };

  const button = cva(['p-2 pr-2.5 flex gap-1 items-center rounded-sm text-sm'], {
    variants: {
      variant: {
        default: 'bg-highlight text-primary',
        active: 'bg-accent text-highlight'
      }
    }
  });
</script>

{#if $client.error}
  <div class="flex h-full w-full flex-col items-center justify-center text-accent">
    <p class="text-xl font-semibold">{$t('error.unexpected.title')}</p>
    <p class="mt-1 opacity-40">{$t('error.unexpected.description')}</p>
  </div>
{:else}
  <div class="flex h-full flex-col">
    <div class="flex-1 p-4">
      <div class="flex flex-col gap-y-6 rounded-sm border bg-primary/2 p-3">
        <div>
          <p class="font-semibold text-sm mb-0.5">{$t('reviews.customer')}</p>
          <p class="lowercase text-sm">{total} {$t('reviews.title')}</p>
        </div>

        <div class="flex flex-col-reverse gap-y-3">
          {#if starDistribution}
            {#each Object.keys(starDistribution) as starItem}
              {@const starItemTyped = getStarItemTyped(starItem)}
              {@const starNumber = parseInt(starItem)}
              <div class="flex items-center justify-between">
                <span>
                  {#each Array(starNumber) as _}
                    <Icon size="sm" icon="star-filled" class="text-gold" />
                  {/each}
                  {#if starNumber < 5}
                    {#each Array(5 - starNumber) as _}
                      <Icon size="sm" icon="star-filled" class="text-gold/20" />
                    {/each}
                  {/if}
                </span>

                <p class="text-sm">{starDistribution[starItemTyped]}</p>
              </div>
            {/each}
          {/if}
        </div>

        <div class="flex items-center justify-between">
          <p class="text-sm">{$t('reviews.modal.overall')}</p>
          {#if $client.isLoading && !averageScore}
            <Skeleton class="h-4 w-10 rounded-md" />
          {:else}
            <p class="text-sm">
              {averageScore.toFixed(1)}
            </p>
          {/if}
        </div>
      </div>

      <div class="mt-8 flex gap-2">
        {#if $client.isLoading && !averageScore}
          <Skeleton class="h-4 w-10 rounded-md" />
        {:else if starDistribution}
          <button
            on:click={() => (star = 'all')}
            class={cn(button({ variant: star === 'all' ? 'active' : 'default' }))}>All</button
          >
          {#each Object.keys(starDistribution) as starItem}
            <button
              on:click={() => (star = getStarItemTyped(starItem))}
              class={cn(button({ variant: star === starItem ? 'active' : 'default' }))}
            >
              {starItem}
              <Icon
                size="sm"
                icon="star-filled"
                class={cn(star === starItem ? 'text-highlight' : 'text-primary')}
              />
            </button>
          {/each}
        {/if}
      </div>

      <div class="mt-8 flex items-center justify-between gap-2 py-2">
        <p class="lowercase text-md text-primary/40">{total} {$t('reviews.title')}</p>

        <Select
          sameWidth={false}
          name="sort"
          variant="link"
          items={[
            { value: 'default', label: $t('reviews.sort.default') },
            { value: 'date', label: $t('reviews.sort.recent') },
            { value: 'rating', label: $t('reviews.sort.rating') }
          ]}
          bind:value={sort}
        />
      </div>

      <div class="mt-4 flex flex-col gap-2">
        {#if $client.isLoading}
          <Skeleton class="h-48 w-full rounded-sm" />
          <Skeleton class="h-48 w-full rounded-sm" />
          <Skeleton class="h-48 w-full rounded-sm" />
        {/if}
        {#each $client.data?.pages || [] as page}
          <YotpoReviews
            class="mb-0"
            reviews={page.reviews}
            {productId}
            {productTitle}
            {productType}
            {productVariant}
          />
        {/each}
      </div>

      <div class="mt-8 flex flex-col items-center">
        <p class="text-primary/40">
          {$t('showing-x-of-y', { x: showing.toString(), y: total.toString() })}
        </p>
        {#if $client.hasNextPage}
          <Button
            name="show-more"
            class="mt-4"
            size="sm"
            loading={$client.isFetchingNextPage}
            variant="ghost"
            on:click={() => $client.fetchNextPage()}>{$t('products.show-more')}</Button
          >
        {/if}
      </div>
    </div>

    <div class="sticky bottom-0 mt-4 border-t bg-background p-4">
      <Button
        name="post"
        size="sm"
        class="w-full"
        on:click={() => {
          drawer.open('review', {
            meta: {
              showCartButtonOnMobile: true,
              titleToShowOnMobileNavigation: 'Reviews'
            }
          });
        }}>{$t('reviews.post')}</Button
      >
    </div>
  </div>
{/if}
